import { HilSignal } from '@cadstrom/backend/shared';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import { EvidenceRowPicks } from '#package-store/package-editor/evidence-row-picks.js';
import { getFloatsAndTiesExtractionContextValue } from '#package-store/package-editor/signals-tab/floats-and-ties-ai-extraction-context.js';
import { applyResolvedFloatsAndTiesToSignals } from '#package-store/package-editor/signals-tab/resolve-directive-floats-and-ties-rows.js';
import { getSignalsAiExtractionContextValue } from '#package-store/package-editor/signals-tab/signals-ai-extraction-context.js';
import { useEvidenceRowPickSelection } from '#package-store/package-family-editor/package-evidence-panel/use-evidence-row-pick-selection.js';
import { SetAiExtractionContext } from '#screens/ai-workbench/ai-extraction-context.js';
import { SignalTable } from '#screens/signal-table/signal-table.js';
import { useTRPC } from '#src/trpc.js';
import { defaultEqualityFn, useAutoSave } from '#utils/use-auto-save/use-auto-save.js';

import type { HilDifferentialPair, HilSignalDto } from '@cadstrom/backend/shared';

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/signals/',
)({
	staticData: {
		navigation: {
			label: 'Package: Signals',
			keywords: ['signals', 'pins'],
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') + 'Signals • Package Editor • Cadstrom',
			},
		],
	}),
	component: SignalsTab,
});

function SignalsTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);

				setServerState({
					signals: res.pkg.signals,
					differentialPairs: res.pkg.differentialPairs,
				});
			},
		}),
	);

	const {
		draftState: { signals: draftSignals, differentialPairs: draftDifferentialPairs },
		setDraftState,
		setServerState,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: {
			signals: pkg.signals,
			differentialPairs: pkg.differentialPairs,
		},
		mutationKeys: trpc.package.update.mutationKey(),
		equalityFn: (serverData, draftData) =>
			defaultEqualityFn(
				serverData.signals.map((signal) => ({ ...signal, isReviewed: false })),
				draftData.signals.map((signal) => ({ ...signal, isReviewed: false })),
			),
		saveFn: async (draftData) => {
			await updatePackage({
				id: pkg.id,
				data: {
					signals: { set: draftData.signals },
					differentialPairs: { set: draftData.differentialPairs },
				},
			});
		},
	});

	const handleAddSignals = (newSignals: HilSignal[]) => {
		setDraftState((prev) => ({ ...prev, signals: [...prev.signals, ...newSignals] }));
	};

	const handleUpdateSignal = (id: string | number, fields: HilSignalDto) => {
		setDraftState((prev) => ({
			...prev,
			signals: prev.signals.map((signal) => (signal.id === id ? HilSignal.merge(signal, fields) : signal)),
		}));
	};

	const handleUpdateSignals = useCallback(
		(newSignals: HilSignal[]) => {
			const newSignalsWithoutIsReviewed = newSignals.map((signal) => ({ ...signal, isReviewed: false }));

			setDraftState((prev) => {
				const signalsToUpdate = newSignalsWithoutIsReviewed
					.filter((s) => draftSignals.find((s2) => s2.id === s.id))
					.map((s) => ({
						id: s.id,
						data: s,
					}));
				const signalsToCreate = newSignalsWithoutIsReviewed.filter((s) => !draftSignals.find((s2) => s2.id === s.id));

				const prevWithUpdates = [...prev.signals];
				for (const s of signalsToUpdate) {
					const index = prevWithUpdates.findIndex((signal) => signal.id === s.id);
					if (index !== -1 && prevWithUpdates[index]) {
						prevWithUpdates[index] = HilSignal.merge(prevWithUpdates[index], s.data);
					}
				}

				return { ...prev, signals: [...prevWithUpdates, ...signalsToCreate] };
			});
		},
		[draftSignals, setDraftState],
	);

	const handleReplaceSignals = useCallback(
		(newSignals: HilSignal[]) => {
			setDraftState((prev) => ({
				...prev,
				signals: newSignals.map((signal) => ({ ...signal, isReviewed: false })),
			}));
		},
		[setDraftState],
	);

	const handleDeleteSignal = (id: string | number) => {
		setDraftState((prev) => ({ ...prev, signals: prev.signals.filter((signal) => signal.id !== id) }));
	};

	// Differential pairs go with the signals: each one references two of them,
	// so an empty signal list would leave every pair dangling.
	const handleDeleteAllSignals = useCallback(() => {
		setDraftState((prev) => ({ ...prev, signals: [], differentialPairs: [] }));
	}, [setDraftState]);

	const handleSetDifferentialPair = (id: string, differentialPair: HilDifferentialPair | 'delete') => {
		setDraftState((prev) => {
			if (differentialPair === 'delete') {
				return { ...prev, differentialPairs: prev.differentialPairs.filter((pair) => pair.id !== id) };
			} else {
				const existingPairIndex = prev.differentialPairs.findIndex((pair) => pair.id === id);
				if (existingPairIndex !== -1) {
					const updatedPairs = [...prev.differentialPairs];
					updatedPairs[existingPairIndex] = differentialPair;
					return { ...prev, differentialPairs: updatedPairs };
				} else {
					return { ...prev, differentialPairs: [...prev.differentialPairs, { ...differentialPair, id }] };
				}
			}
		});
	};

	const extractionContextValue = useMemo(() => {
		return getSignalsAiExtractionContextValue({
			sigmaModelType: pkg.sigmaModelType,
			replaceSignals: handleReplaceSignals,
			appendSignals: handleUpdateSignals,
		});
	}, [pkg.sigmaModelType, handleReplaceSignals, handleUpdateSignals]);

	const floatsAndTiesContextValue = useMemo(() => {
		return getFloatsAndTiesExtractionContextValue({
			signals: draftSignals,
			mergeFloatsAndTies: (rows) =>
				setDraftState((prev) => ({
					...prev,
					signals: applyResolvedFloatsAndTiesToSignals(prev.signals, rows, 'merge'),
				})),
			replaceFloatsAndTies: (rows) =>
				setDraftState((prev) => ({
					...prev,
					signals: applyResolvedFloatsAndTiesToSignals(prev.signals, rows, 'replace'),
				})),
		});
	}, [draftSignals, setDraftState]);

	const { selectedRow: selectedRowId, select: selectRow } = useEvidenceRowPickSelection<string>();
	const handleSelectedRowChange = useCallback(
		(rowId: string | null, signal: HilSignal | null) =>
			selectRow(rowId, signal === null ? null : EvidenceRowPicks.forSignal(pkg.id, signal.name)),
		[pkg.id, selectRow],
	);

	return (
		<div ref={saveAreaContainerRef} className='relative grid min-h-0 grow basis-0 place-items-stretch px-3 py-2'>
			<SignalTable
				signals={draftSignals}
				sigmaModelType={pkg.sigmaModelType}
				differentialPairs={draftDifferentialPairs}
				handleAddSignals={handleAddSignals}
				handleUpdateSignal={handleUpdateSignal}
				handleDeleteSignal={handleDeleteSignal}
				handleDeleteAllSignals={handleDeleteAllSignals}
				handleSetDifferentialPair={handleSetDifferentialPair}
				selectedRowId={selectedRowId}
				onSelectedRowChange={handleSelectedRowChange}
				showSaveButton={{
					saveDisabled: !isDirty,
					onClickSave: handleSave,
					isSaving: updatePackageIsPending,
				}}
			/>

			<SetAiExtractionContext value={extractionContextValue} />
			<SetAiExtractionContext value={floatsAndTiesContextValue} />
		</div>
	);
}
